import type { MediaCacheEntry } from '../types/mediaCache';

const DB_NAME = 'iarts_media_cache_v1';
const DB_VERSION = 2;
const STORE_NAME = 'media';

const hasIndexedDbSupport = (): boolean =>
  typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

const promisifyRequest = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 请求失败'));
  });

const tryCreateIndexes = (store: IDBObjectStore | undefined): void => {
  if (!store) return;
  if (!store.indexNames.contains('nodeGraphId')) {
    store.createIndex('nodeGraphId', 'nodeGraphId', { unique: false });
  }
  if (!store.indexNames.contains('cachedAt')) {
    store.createIndex('cachedAt', 'cachedAt', { unique: false });
  }
};

class MediaCacheStoreImpl {
  private dbPromise: Promise<IDBDatabase | null> | null = null;

  private async openDatabase(): Promise<IDBDatabase | null> {
    if (!hasIndexedDbSupport()) {
      return null;
    }
    if (!this.dbPromise) {
      this.dbPromise = new Promise<IDBDatabase | null>((resolve, reject) => {
        try {
          const request = indexedDB.open(DB_NAME, DB_VERSION);
          request.onupgradeneeded = () => {
            const db = request.result;
            let store: IDBObjectStore | undefined;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              store = db.createObjectStore(STORE_NAME, { keyPath: 'cacheId' });
            } else if (request.transaction) {
              store = request.transaction.objectStore(STORE_NAME);
            }
            tryCreateIndexes(store);
          };
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error ?? new Error('打开媒体缓存数据库失败'));
          request.onblocked = () => reject(new Error('媒体缓存数据库被其他连接阻塞'));
        } catch (error) {
          reject(error);
        }
      });
    }

    try {
      return await this.dbPromise;
    } catch (error) {
      console.error('[MediaCacheStore] 打开 IndexedDB 失败', error);
      this.dbPromise = null;
      return null;
    }
  }

  private async runStoreOperation<R>(
    mode: IDBTransactionMode,
    executor: (store: IDBObjectStore) => Promise<R>
  ): Promise<R | null> {
    const db = await this.openDatabase();
    if (!db) return null;

    return new Promise<R | null>((resolve, reject) => {
      try {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        let operationResult: R | undefined;

        Promise.resolve(executor(store))
          .then((result) => {
            operationResult = result;
          })
          .catch((error) => {
            reject(error);
            try {
              tx.abort();
            } catch {
              // ignore abort error
            }
          });

        tx.oncomplete = () => resolve(operationResult ?? null);
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB 事务失败'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB 事务被中断'));
      } catch (error) {
        reject(error);
      }
    }).catch((error) => {
      console.warn('[MediaCacheStore] 事务执行失败', error);
      return null;
    });
  }

  async put(entry: MediaCacheEntry): Promise<void> {
    await this.runStoreOperation('readwrite', async (store) => {
      try {
        await promisifyRequest(store.put(entry));
      } catch (error) {
        console.warn('[MediaCacheStore] 写入缓存失败', error);
      }
      return null;
    });
  }

  async get(cacheId: string): Promise<MediaCacheEntry | null> {
    const result = await this.runStoreOperation('readonly', async (store) => {
      try {
        const request = store.get(cacheId) as IDBRequest<MediaCacheEntry | undefined>;
        const entry = await promisifyRequest(request);
        return entry ?? null;
      } catch (error) {
        console.warn('[MediaCacheStore] 读取缓存失败', error);
        return null;
      }
    });
    return result ?? null;
  }

  async delete(cacheId: string): Promise<void> {
    await this.runStoreOperation('readwrite', async (store) => {
      try {
        await promisifyRequest(store.delete(cacheId));
      } catch (error) {
        console.warn('[MediaCacheStore] 删除缓存失败', cacheId, error);
      }
      return null;
    });
  }

  async deleteByNodeGraph(nodeGraphId: string): Promise<void> {
    await this.runStoreOperation('readwrite', async (store) => {
      const cacheIds: string[] = [];
      try {
        if (store.indexNames.contains('nodeGraphId')) {
          const index = store.index('nodeGraphId');
          const request = index.getAll(IDBKeyRange.only(nodeGraphId)) as IDBRequest<
            MediaCacheEntry[]
          >;
          const entries = await promisifyRequest(request);
          cacheIds.push(...entries.map((item) => item.cacheId));
        } else {
          const request = store.getAll() as IDBRequest<MediaCacheEntry[]>;
          const entries = await promisifyRequest(request);
          for (const item of entries) {
            if (item.nodeGraphId === nodeGraphId) {
              cacheIds.push(item.cacheId);
            }
          }
        }
      } catch (error) {
        console.warn('[MediaCacheStore] 读取节点缓存列表失败', error);
      }

      for (const id of cacheIds) {
        try {
          await promisifyRequest(store.delete(id));
        } catch (error) {
          console.warn('[MediaCacheStore] 删除节点缓存失败', id, error);
        }
      }
      return null;
    });
  }

  async cleanup(maxAge: number, maxSize: number): Promise<{ deletedCount: number }> {
    const now = Date.now();
    const result = await this.runStoreOperation('readwrite', async (store) => {
      const toDelete: MediaCacheEntry[] = [];
      try {
        const request = store.getAll() as IDBRequest<MediaCacheEntry[]>;
        const entries = await promisifyRequest(request);
        const ageThreshold = maxAge > 0 ? now - maxAge : null;
        const sorted = [...entries].sort((a, b) => a.cachedAt - b.cachedAt);
        let currentSize = 0;

        for (const entry of sorted) {
          const size = Number(entry.sizeBytes) || entry.data.size || 0;
          const expired = ageThreshold !== null && entry.cachedAt < ageThreshold;
          if (expired) {
            toDelete.push(entry);
            continue;
          }
          if (maxSize > 0 && currentSize + size > maxSize) {
            toDelete.push(entry);
            continue;
          }
          currentSize += size;
        }
      } catch (error) {
        console.warn('[MediaCacheStore] 读取缓存列表失败', error);
      }

      for (const entry of toDelete) {
        try {
          await promisifyRequest(store.delete(entry.cacheId));
        } catch (error) {
          console.warn('[MediaCacheStore] 清理缓存失败', entry.cacheId, error);
        }
      }
      return { deletedCount: toDelete.length };
    });

    return result ?? { deletedCount: 0 };
  }

  async getStats(): Promise<{ totalSize: number; count: number }> {
    const result = await this.runStoreOperation('readonly', async (store) => {
      try {
        const request = store.getAll() as IDBRequest<MediaCacheEntry[]>;
        const entries = await promisifyRequest(request);
        let totalSize = 0;
        for (const entry of entries) {
          totalSize += Number(entry.sizeBytes) || entry.data.size || 0;
        }
        return { totalSize, count: entries.length };
      } catch (error) {
        console.warn('[MediaCacheStore] 统计缓存失败', error);
        return { totalSize: 0, count: 0 };
      }
    });

    return result ?? { totalSize: 0, count: 0 };
  }

  /** 按项目 ID 统计媒体大小 */
  async getStatsByNodeGraph(): Promise<Map<string, { size: number; count: number }>> {
    const result = await this.runStoreOperation('readonly', async (store) => {
      try {
        const request = store.getAll() as IDBRequest<MediaCacheEntry[]>;
        const entries = await promisifyRequest(request);
        const statsMap = new Map<string, { size: number; count: number }>();

        for (const entry of entries) {
          const graphId = entry.nodeGraphId;
          const size = Number(entry.sizeBytes) || entry.data.size || 0;
          const existing = statsMap.get(graphId) ?? { size: 0, count: 0 };
          statsMap.set(graphId, {
            size: existing.size + size,
            count: existing.count + 1,
          });
        }

        return statsMap;
      } catch (error) {
        console.warn('[MediaCacheStore] 按项目统计缓存失败', error);
        return new Map<string, { size: number; count: number }>();
      }
    });

    return result ?? new Map();
  }
}

export const MediaCacheStore = new MediaCacheStoreImpl();
